/**
 * Top-up / Create-eSIM modal (B-009 frontend, global eSIM model 2026-05-03).
 *
 * Single modal, two modes:
 *   - **create**: opens with `{ countrySlug?, countryName?, flag? }`. The
 *     country fields are PURELY informational — they record which marketing
 *     page the click came from. The eSIM itself is global. Submitting calls
 *     `POST /api/esims` with the amount; the backend pre-creates the eSIM
 *     (status='reserved', 30 min TTL), generates a 10-digit displayId, embeds
 *     it in the NowPayments invoice `order_description`, and returns the
 *     invoice URL. Frontend redirects to NP. Once IPN confirms payment, the
 *     eSIM flips to 'active' with the paid amount as balance.
 *   - **topup**:  opens with `{ esimId, displayId? }`. Submitting calls
 *     `POST /api/esims/:id/topup` → invoice → redirect → balance lands on IPN.
 *
 * Email field: shown only if `Auth.getUser().email` is null. Required for
 * Telegram-only signups (Telegram doesn't expose email). Saved server-side
 * via maybeSaveEmail — only writes if user.email was null.
 */
import { ModalManager } from './manager';
import { Auth } from '../auth';
import * as api from '../api';
import { escapeHtml } from '../utils';
import { t } from '../i18n';
import { track } from '../analytics';
import { showToast } from '../toast';
import { Router } from '../router';
import { getConfig, isPrimed } from '../config';
import {
  mainButton as tgMainButton,
  haptic as tgHaptic,
  closingConfirmation as tgClosingConfirmation,
  tgIsAvailable,
  tgOpenLink,
} from '../telegram-mini-app';

const TITLE_ID = 'topup-modal-title';
const MIN_USDT = 20;
// Capped at 200 USDT per transaction (matches backend MAX_TOPUP_USDT). Users
// who need more can do multiple top-ups; capping limits exposure on a stolen
// session.
const MAX_USDT = 200;
// Quick-pick presets shown above the input — covers the most common
// trip lengths (weekend / 1 week / 2 weeks / month-ish at typical rates).
const PRESETS = [20, 50, 75, 100] as const;

const PROMO_REASONS = new Set(['unknown_code', 'inactive', 'expired', 'not_yet_valid', 'cap_reached',
  'per_user_cap_reached', 'min_topup_not_met', 'first_purchase_only']);

/**
 * What the person reads when the invoice cannot be created (2026-09-29).
 * Every refusal — payments paused, the account's eSIM limit, an unpaid
 * reservation already open, too many abandoned invoices, a provider outage —
 * used to read "Couldn't create invoice: HTTP 409". The server names the
 * reason in `error`; this turns it into something to act on.
 */
type Failure = { text: string; goToReservation?: boolean };
type ErrBody = { error?: string; reason?: string; cap?: number };

const FAILURE_BY_CODE: Record<string, (b: ErrBody) => Failure> = {
  payments_disabled: () => ({ text: t('maintenance.paymentsOffTooltip') }),
  'payments not configured': () => ({ text: t('maintenance.paymentsOffTooltip') }),
  esim_cap_reached: (b) => ({ text: b.cap ? t('topup.errors.cap', { cap: b.cap }) : t('topup.errors.generic') }),
  esim_already_reserved: () => ({ text: t('dash.esims.alreadyReservedWithPay'), goToReservation: true }),
  too_many_abandoned_reservations: () => ({ text: t('topup.errors.abandoned') }),
  promo_invalid: (b) => ({
    text: t(`topup.promoBad.${PROMO_REASONS.has(b.reason ?? '') ? b.reason : 'unknown_code'}` as Parameters<typeof t>[0]),
  }),
  user_suspended: () => ({ text: t('topup.errors.suspended') }),
  esim_inactive: () => ({ text: t('topup.errors.inactive') }),
  esim_not_found: () => ({ text: t('topup.errors.inactive') }),
};

export function topupErrorText(err: unknown): Failure {
  if (!(err instanceof api.ApiError)) return { text: t('topup.errors.network') };
  const body = (err.body ?? {}) as ErrBody;
  const byCode = body.error ? FAILURE_BY_CODE[body.error] : undefined;
  if (byCode) return byCode(body);
  if (err.status === 429) return { text: t('topup.errors.rateLimited') };
  return { text: t(err.status >= 500 ? 'topup.errors.provider' : 'topup.errors.generic') };
}

/** Why paying cannot start at all right now, or null. Checked when the form
 *  would open, so no one fills in a form that can only fail. */
function payBlockedReason(): string | null {
  if (isPrimed() && !getConfig().paymentsEnabled) return t('maintenance.paymentsOffTooltip');
  if (Auth.getUser()?.suspended) return t('topup.errors.suspended');
  return null;
}

function esimLabelHtml(m: TopUpMode): string {
  return m.kind === 'topup' && m.label ? `<p class="topup-modal__esim mono">${escapeHtml(m.label)}</p>` : '';
}

export type TopUpMode =
  | { kind: 'create'; countrySlug?: string; countryName?: string; flag?: string }
  /** `label`: which eSIM, as the person knows it ("iPad · 4470005678"). Picked
   *  from a list of several, "Top up your eSIM" alone did not say which. */
  | { kind: 'topup'; esimId: string; displayId?: string; label?: string };

export function openTopUp(mode?: TopUpMode): void {
  if (!Auth.isAuthed()) {
    document.dispatchEvent(new CustomEvent('roamzy:open-login', {
      detail: { redirectTo: '/dashboard/esims' },
    }));
    return;
  }

  // Payments paused (admin STOP switch). The header button is disabled by
  // applyPaymentsState, but the eSIM cards render after it runs and carry
  // their own "Top up" — those opened a full form that ended in "HTTP 503".
  // Every entry to paying goes through here, so the check lives here.
  const blocked = payBlockedReason();
  if (blocked) {
    showToast(blocked, 'info');
    return;
  }

  // Default: open the global "create new eSIM" path with no country context.
  const m: TopUpMode = mode ?? { kind: 'create' };
  const isCreate = m.kind === 'create';

  // Country chip is PURELY informational ("you came from /countries/jp"). The
  // eSIM itself is global; we never lock it to that country.
  const countryChip = isCreate && m.countryName
    ? `<span class="topup-modal__country-chip">${m.flag ?? '🌐'} ${escapeHtml(m.countryName)}</span>`
    : '';

  const headerTitle = isCreate ? t('topup.createTitleGlobal') : t('topup.topupTitleGlobal');
  const headerSub = isCreate ? t('topup.createSubGlobal') : t('topup.topupSubGlobal');
  const ctaLabel = isCreate ? t('topup.createCta') : t('topup.topupCta');

  const user = Auth.getUser();
  const needsEmail = user && !user.email;
  const emailFieldHtml = needsEmail
    ? `<label class="topup-form__field">
    <span class="topup-form__label">${t('topup.emailLabel')}</span>
    <input
      type="email"
      name="email"
      maxlength="254"
      autocomplete="email"
      class="topup-form__input"
      data-topup-email
      required
    />
    <span class="topup-form__hint">${t('topup.emailHint')}</span>
  </label>`
    : '';

  // Track that the modal opened — separate from "submit clicked". Lets us
  // measure drop-off between "user clicked top-up" and "user actually paid".
  track('TopUpModalOpen', { kind: m.kind });

  ModalManager.open({
    id: 'topup',
    panelClass: 'topup-modal',
    labelledBy: TITLE_ID,
    render() {
      return `
<div class="topup-trial" data-topup-trial hidden></div>
<h2 id="${TITLE_ID}" class="topup-modal__title">${escapeHtml(headerTitle)}</h2>
<p class="topup-modal__sub">${escapeHtml(headerSub)}</p>
${countryChip}
${esimLabelHtml(m)}

<form class="topup-form" data-topup-form>
  <label class="topup-form__field">
    <span class="topup-form__label">${t('topup.amountLabel')}</span>
    <div class="topup-form__presets" role="group" aria-label="${t('topup.presetsAriaLabel')}">
      ${PRESETS
        .map((n, i) => `<button type="button" class="topup-preset${i === 0 ? ' is-active' : ''}" data-topup-preset="${n}">$${n}</button>`)
        .join('')}
    </div>
    <div class="topup-form__amount-row">
      <input
        type="number"
        name="amount"
        min="${MIN_USDT}"
        max="${MAX_USDT}"
        step="1"
        value="20"
        inputmode="numeric"
        autocomplete="off"
        class="topup-form__input mono"
        data-topup-amount
        required
      />
    </div>
    <span class="topup-form__hint">${t('topup.hint', { min: MIN_USDT, max: MAX_USDT })}</span>
  </label>

  ${emailFieldHtml}

  <label class="topup-form__field">
    <span class="topup-form__label">${t('topup.promoLabel')}</span>
    <input
      type="text"
      name="promoCode"
      maxlength="32"
      autocomplete="off"
      autocapitalize="characters"
      class="topup-form__input mono"
      placeholder="${t('topup.promoPlaceholder')}"
      data-topup-promo
    />
    <span class="topup-form__hint" data-topup-promo-status>${t('topup.promoHint')}</span>
  </label>

  ${isCreate ? `<aside class="topup-form__reservation-hint">${t('topup.reservationHint')}</aside>` : ''}

  <button type="submit" class="btn btn--accent btn--full" data-topup-submit>${escapeHtml(ctaLabel)}</button>
  <p class="topup-form__error" data-topup-error hidden></p>
</form>

<p class="topup-form__legal">${t('topup.legal')}</p>`;
    },
    onMount(panel) {
      // The free first eSIM, offered at the exact moment the wall appears.
      //
      // The dashboard card was not enough: the first eligible visitor after
      // the trial shipped went from signup to a $20 invoice in 18 seconds and
      // never claimed it. This modal IS where that decision is made, so the
      // offer is repeated here — only for a `create` (never a top-up of an
      // existing eSIM) and only when the server says the person is eligible.
      if (isCreate) {
        void (async () => {
          const slot = panel.querySelector<HTMLElement>('[data-topup-trial]');
          if (!slot) return;
          const { fetchTrialEligibility, claimTrial, trialErrorText } = await import('../routes/dashboard/esims');
          const el = await fetchTrialEligibility();
          if (!el?.eligible) return;
          const credit = `$${(el.credit_micro_usdt / 1_000_000).toFixed(2)}`;
          slot.innerHTML = `
  <h3 class="topup-trial__title" data-i18n="topup.trial.title">${escapeHtml(t('topup.trial.title'))}</h3>
  <p class="topup-trial__body">${escapeHtml(t('topup.trial.body', { credit }))}</p>
  <div class="topup-trial__actions">
    <button type="button" class="btn btn--accent" data-topup-trial-claim data-i18n="topup.trial.cta">${escapeHtml(t('topup.trial.cta'))}</button>
    <button type="button" class="topup-trial__dismiss" data-topup-trial-dismiss data-i18n="topup.trial.dismiss">${escapeHtml(t('topup.trial.dismiss'))}</button>
  </div>
  <span class="topup-trial__status" data-topup-trial-status hidden></span>`;
          slot.hidden = false;
          track('TrialBannerShown', { kind: 'create' });

          slot.querySelector('[data-topup-trial-dismiss]')?.addEventListener('click', () => {
            slot.hidden = true;
            track('TrialBannerDismiss', { kind: 'create' });
          });
          const claimBtn = slot.querySelector<HTMLButtonElement>('[data-topup-trial-claim]');
          const statusEl = slot.querySelector<HTMLElement>('[data-topup-trial-status]');
          claimBtn?.addEventListener('click', () => {
            claimBtn.disabled = true;
            if (statusEl) { statusEl.textContent = t('dash.trial.claiming'); statusEl.hidden = false; }
            track('TrialBannerClick', { kind: 'create' });
            void (async () => {
              const r = await claimTrial();
              if (!r.ok) {
                const why = trialErrorText(r.error);
                claimBtn.disabled = why.settled;
                if (statusEl) statusEl.textContent = why.text;
                return;
              }
              ModalManager.close('topup');
              showToast(t('dash.trial.done'), 'success');
              Router.navigate('/dashboard/esims');
            })();
          });
        })();
      }

      const form = panel.querySelector<HTMLFormElement>('[data-topup-form]');
      const submit = panel.querySelector<HTMLButtonElement>('[data-topup-submit]');
      const errorEl = panel.querySelector<HTMLElement>('[data-topup-error]');
      if (!form || !submit) return;

      const amountInput = panel.querySelector<HTMLInputElement>('[data-topup-amount]');
      const presetButtons = panel.querySelectorAll<HTMLButtonElement>('[data-topup-preset]');
      const promoInput = panel.querySelector<HTMLInputElement>('[data-topup-promo]');
      const promoStatus = panel.querySelector<HTMLElement>('[data-topup-promo-status]');

      // Promo state — debounced 500ms validate; result drives submit + UI.
      let promoBonusMicroUsdt = 0;
      let promoOk = false;
      let promoDebounce: ReturnType<typeof setTimeout> | null = null;
      let promoReqId = 0;

      const setPromoStatus = (text: string, kind: 'hint' | 'ok' | 'bad'): void => {
        if (!promoStatus) return;
        promoStatus.textContent = text;
        promoStatus.classList.remove('topup-form__hint--ok', 'topup-form__hint--bad');
        if (kind === 'ok') promoStatus.classList.add('topup-form__hint--ok');
        if (kind === 'bad') promoStatus.classList.add('topup-form__hint--bad');
      };

      const runPromoCheck = async (): Promise<void> => {
        if (!promoInput) return;
        const code = promoInput.value.trim().toUpperCase();
        if (!code) {
          promoOk = false; promoBonusMicroUsdt = 0;
          setPromoStatus(t('topup.promoHint'), 'hint');
          return;
        }
        if (code.length < 2) return;
        const amountUsdt = Math.floor(Number(amountInput?.value));
        if (!Number.isFinite(amountUsdt) || amountUsdt < MIN_USDT) {
          setPromoStatus(t('topup.promoHint'), 'hint');
          return;
        }
        const reqId = ++promoReqId;
        setPromoStatus(t('topup.promoChecking'), 'hint');
        try {
          const r = await api.validatePromo(code, amountUsdt);
          if (reqId !== promoReqId) return;  // newer request in flight, ignore
          if (r.ok && typeof r.bonusUsdt === 'number') {
            promoOk = true;
            promoBonusMicroUsdt = r.bonusMicroUsdt ?? Math.round(r.bonusUsdt * 1_000_000);
            setPromoStatus(t('topup.promoOk', { bonus: r.bonusUsdt.toFixed(0) }), 'ok');
          } else {
            promoOk = false; promoBonusMicroUsdt = 0;
            const reason = (r.reason ?? 'unknown_code') as
              'unknown_code'|'inactive'|'expired'|'not_yet_valid'|'cap_reached'
              |'per_user_cap_reached'|'min_topup_not_met'|'first_purchase_only';
            setPromoStatus(t(`topup.promoBad.${reason}` as Parameters<typeof t>[0]), 'bad');
          }
        } catch {
          if (reqId !== promoReqId) return;
          promoOk = false; promoBonusMicroUsdt = 0;
          setPromoStatus(t('topup.promoBad.unknown_code'), 'bad');
        }
      };
      const schedulePromoCheck = (): void => {
        if (promoDebounce) clearTimeout(promoDebounce);
        promoDebounce = setTimeout(runPromoCheck, 500);
      };
      promoInput?.addEventListener('input', schedulePromoCheck);
      // Re-validate when amount changes (might cross min_topup threshold)
      amountInput?.addEventListener('input', schedulePromoCheck);

      // Highlight whichever preset matches the current input value (or none if
      // the user typed a custom amount).
      const syncPresetActive = (): void => {
        const cur = Number(amountInput?.value);
        presetButtons.forEach((b) => {
          const v = Number(b.dataset.topupPreset);
          b.classList.toggle('is-active', Number.isFinite(cur) && cur === v);
        });
      };
      // The "≈ $20" box beside the field is gone (2026-09-29): stablecoins
      // credit 1:1, so it only echoed the number typed next to it.
      const updateEquiv = (): void => { syncPresetActive(); };
      amountInput?.addEventListener('input', updateEquiv);

      // Native browser validation tooltips (`<input type="number" min/max>`)
      // localise their text from OS / browser language settings, NOT from
      // our `lang` attribute. So an English-locale page can still show
      // "Значение должно быть больше или равно 20" if the user's Chrome
      // is set to RU. Override with our own i18n string via setCustomValidity
      // — wired on `invalid`. Cleared on every `input` so the tooltip
      // stops showing the stale message after the user fixes the value.
      // Operator caught this 2026-05-08 (EN page, RU tooltip).
      if (amountInput) {
        const validateAmount = (): void => {
          const v = Number(amountInput.value);
          if (Number.isNaN(v) || v < MIN_USDT) {
            amountInput.setCustomValidity(t('topup.belowMin', { min: MIN_USDT }));
          } else if (v > MAX_USDT) {
            amountInput.setCustomValidity(t('topup.aboveMax', { max: MAX_USDT }));
          } else {
            amountInput.setCustomValidity('');
          }
        };
        amountInput.addEventListener('input', validateAmount);
        amountInput.addEventListener('invalid', validateAmount);
      }

      // Preset click → set input + sync UI. Native button click fires before
      // any form submission, and `type="button"` keeps it from submitting.
      presetButtons.forEach((btn) => {
        btn.addEventListener('click', () => {
          if (!amountInput) return;
          amountInput.value = btn.dataset.topupPreset ?? '';
          updateEquiv();
          // Tactile feedback for preset taps inside TG Mini App — these
          // are secondary actions (not the primary submit), so light tap.
          tgHaptic.tapLight();
          // Bring focus back to the input so a quick edit-after-pick works.
          amountInput.focus();
          amountInput.select();
        });
      });

      // === Telegram Mini App integration ===============================
      // MainButton mirrors the in-modal submit button: same form, same
      // handler, single primary CTA. Our own submit button is hidden in
      // TG context via CSS (`body.is-telegram-webapp .topup-form .btn--full
      // { display: none }`) so users see one CTA, not two.
      //
      // ClosingConfirmation enabled mid-submit so a user accidentally
      // swiping away during the NowPayments redirect gets prompted —
      // otherwise their reserved eSIM is orphaned for 30 min.
      let tgMainButtonUnmount: (() => void) | null = null;
      let tgClosingConfUnmount: (() => void) | null = null;
      const inTelegram = tgIsAvailable();

      const computeCtaText = (): string => {
        const a = Math.floor(Number(amountInput?.value));
        const valid = Number.isFinite(a) && a >= MIN_USDT && a <= MAX_USDT;
        if (!valid) return ctaLabel;
        // "Create eSIM · $20" / "Top up · $20" — narrow enough for the
        // TG MainButton's single-line horizontal slot on small phones.
        // The in-page CTA ends in "→"; on the native button it landed mid-label
        // ("Top up → · $50").
        return `${ctaLabel.replace(/\s*→\s*$/, '')} · $${a}`;
      };

      const computeCtaEnabled = (): boolean => {
        const a = Math.floor(Number(amountInput?.value));
        if (!Number.isFinite(a) || a < MIN_USDT || a > MAX_USDT) return false;
        if (needsEmail) {
          const emailEl = panel.querySelector<HTMLInputElement>('[data-topup-email]');
          const v = (emailEl?.value || '').trim();
          if (!v || !v.includes('@')) return false;
        }
        return true;
      };

      const syncMainButton = (): void => {
        if (!inTelegram) return;
        tgMainButton.update({
          text: computeCtaText(),
          enabled: computeCtaEnabled(),
        });
      };

      if (inTelegram) {
        tgMainButtonUnmount = tgMainButton.push({
          text: computeCtaText(),
          enabled: computeCtaEnabled(),
          onClick: async () => {
            // Strong haptic on the primary action — operator quality bar.
            tgHaptic.tap();
            // requestSubmit fires native form validation + onSubmit below
            // (which has all the validation + API + redirect logic). We
            // don't duplicate that here.
            form.requestSubmit();
            // The MainButton wrapper auto-shows progress until this
            // Promise resolves. onSubmit's own loading state (submit
            // button disabled + label change) takes over from this point.
            await Promise.resolve();
          },
        });

        // Live-update MainButton on user input. amount change → button
        // text shows new amount; email/amount validity → button enabled
        // flag flips.
        amountInput?.addEventListener('input', syncMainButton);
        const emailEl = panel.querySelector<HTMLInputElement>('[data-topup-email]');
        emailEl?.addEventListener('input', syncMainButton);
      }

      const onSubmit = async (e: Event): Promise<void> => {
        e.preventDefault();
        if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

        const fd = new FormData(form);
        const amountUsdt = Math.floor(Number(fd.get('amount')));
        // Chain picker removed 2026-05-06 — operator whitelisted only
        // 11 stablecoin chains (TRC20/BSC/Polygon/OP/Arbitrum/SOL/TON)
        // in the NowPayments dashboard. Modal asks only for amount;
        // user picks the chain on NP's checkout page from the curated
        // list. Cleaner UX, fits one screen on mobile.
        const email = String(fd.get('email') || '').trim();
        const promoCodeRaw = String(fd.get('promoCode') || '').trim().toUpperCase();
        // Only attach promoCode if it passed validation. A typed-but-invalid
        // code shouldn't propagate — backend would 400 anyway, but cleaner UX
        // is to drop it silently and let the user fix the field.
        const promoCode = promoOk && promoCodeRaw ? promoCodeRaw : undefined;

        if (!Number.isFinite(amountUsdt) || amountUsdt < MIN_USDT || amountUsdt > MAX_USDT) {
          if (errorEl) {
            errorEl.textContent = t('topup.amountError', { min: MIN_USDT, max: MAX_USDT });
            errorEl.hidden = false;
          }
          // Tactile signal that the action failed — matches the visual
          // red error text appearing below the form. No-op outside TG.
          tgHaptic.error();
          return;
        }

        if (needsEmail && !email) {
          if (errorEl) {
            errorEl.textContent = t('topup.emailRequired');
            errorEl.hidden = false;
          }
          tgHaptic.error();
          return;
        }

        submit.disabled = true;
        submit.textContent = t('topup.creating');
        // Disable MainButton + change label while the API call runs +
        // NowPayments redirect happens. Inside TG, this matches the
        // submit-button-disabled state visually.
        if (inTelegram) {
          tgMainButton.update({ text: t('topup.creating'), enabled: false });
          // Enable "are you sure you want to close?" TG prompt — protects
          // against accidental swipe-away while the reservation is in
          // flight (would orphan a `reserved` eSIM for 30 min).
          tgClosingConfUnmount = tgClosingConfirmation.enable();
        }

        // Track submit-attempted before NP redirect so we can measure
        // drop-off between modal-open and successfully-submitted form.
        track('TopUpSubmit', {
          kind: m.kind,
          amountUsdt,
          ...(promoCode ? { hasPromo: true } : {}),
        });

        try {
          const resp = m.kind === 'create'
            ? await api.createEsim({
                amountUsdt,
                ...(m.countrySlug ? { countrySlug: m.countrySlug } : {}),
                ...(email ? { email } : {}),
                ...(promoCode ? { promoCode } : {}),
              })
            : await api.topUpEsim(m.esimId, {
                amountUsdt,
                ...(email ? { email } : {}),
                ...(promoCode ? { promoCode } : {}),
              });
          // Use the bonus we computed during validate so we can log/track,
          // even though the modal is about to navigate away to NowPayments.
          void promoBonusMicroUsdt;
          // Optimistically update the cached user.email so the next time the
          // modal opens we don't ask again. (Server has authoritative state;
          // /api/me will re-sync on next page load.)
          if (email && user) {
            Auth.updateUserLocal({ email });
          }
          // Telegram Mini App: navigating the SAME webview to NowPayments
          // strands the user on np.io — TG's system "Назад" closes the
          // whole Mini App rather than going back to roamzy. Use TG's
          // openLink() to spawn an external TG in-app browser instead;
          // the Mini App stays alive on /dashboard/esims with the just-
          // reserved eSIM. User pays in the spawned browser, swipes back
          // to the Mini App (still on dashboard), and the 60s auto-refresh
          // + visibility-change refresh picks up the IPN-driven status
          // flip (reserved → active). Operator caught the same-webview
          // dead-end 2026-05-11 — see ops-log "Mini App TG navigation fix".
          if (inTelegram) {
            tgOpenLink(resp.invoiceUrl);
            // We're no longer mid-redirect — drop the close-guard so the
            // user can freely leave the Mini App if they want (the payment
            // continues in the external browser regardless).
            try { tgClosingConfUnmount?.(); tgClosingConfUnmount = null; } catch { /* */ }
            // Close the modal — its job is done.
            ModalManager.close('topup');
            // Move the Mini App user to the dashboard so they can see the
            // freshly-reserved eSIM card right below their eyes while the
            // payment happens in the external TG browser. If we're already
            // on /dashboard/esims this is a no-op (Router.navigate skips
            // self-targets), and the existing dashboard auto-refresh
            // (visibilitychange + 60s interval) picks up the new card on
            // its own. On country page → routes user to the right place.
            Router.navigate('/dashboard/esims');
            // 7s toast — enough to read while glancing at the new reserved
            // eSIM card on the grid. Triggers a light haptic per toast.ts
            // wiring for 'info' kind.
            showToast(t('toast.topupOpenedInBrowser'), 'info', 7000);
            return;
          }
          window.location.href = resp.invoiceUrl;
        } catch (err) {
          submit.disabled = false;
          submit.textContent = ctaLabel;
          const failure = topupErrorText(err);
          if (failure.goToReservation) {
            // Already holding an unpaid reservation: its card has the live
            // "Pay now". Same answer the pre-check in reservation-guard gives.
            ModalManager.close('topup');
            showToast(failure.text, 'info', 7000);
            Router.navigate('/dashboard/esims');
            return;
          }
          if (errorEl) {
            errorEl.textContent = failure.text;
            errorEl.hidden = false;
          }
          // Failure haptic + restore TG MainButton + drop the close-guard
          // (user is no longer mid-submit, free to swipe away if they want).
          tgHaptic.error();
          if (inTelegram) {
            tgMainButton.update({ text: computeCtaText(), enabled: computeCtaEnabled() });
            try { tgClosingConfUnmount?.(); tgClosingConfUnmount = null; } catch { /* */ }
          }
        }
      };

      form.addEventListener('submit', onSubmit);
      return () => {
        form.removeEventListener('submit', onSubmit);
        // Pop TG MainButton frame + clear closingConfirmation if still
        // active (e.g. modal force-closed during submit). Both are no-ops
        // outside Telegram, and idempotent inside it.
        try { tgMainButtonUnmount?.(); } catch { /* */ }
        try { tgClosingConfUnmount?.(); } catch { /* */ }
      };
    },
  });
}
